import React from 'react';
import { Pressable, View } from 'react-native';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Card } from '@/components/gluestack/card';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { useTranslation } from 'react-i18next';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import { communities } from '@/constants/communities';
import { StarAward01Icon } from '@hugeicons-pro/core-solid-rounded';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';

interface PlanCardWebProps {
  title: string;
  subTitle?: string;
  price: number;
  period: string;
  renewalDays: number;
  savings?: string;
  onSubscribe: () => void;
  isPremium: boolean;
  isRecommended?: boolean;
  className?: string;
}

export const PlanCardWeb: React.FC<PlanCardWebProps> = ({
  title,
  subTitle,
  price,
  period,
  renewalDays,
  savings,
  onSubscribe,
  isPremium,
  isRecommended = false,
  className,
}) => {
  const { t } = useTranslation();
  const openWebPage = useOpenWebPage();

  const dollars = Math.floor(price);
  const cents = Math.round((price - dollars) * 100);

  const chasablePlanName = communities.chasabl.premiumPlan;
  const chasableDomain = communities.chasabl.domain;

  const handleChasableLinkPress = () => {
    openWebPage(`https://www.${chasableDomain}`);
  };

  const renderSubtitle = () => {
    if (!subTitle) return null;
    
    const parts = subTitle.split(chasablePlanName);
    if (parts.length === 1) {
      return (
        <Text size="sm" className="text-center text-typography-600">
          {subTitle}
        </Text>
      );
    }
    
    // If the subTitle contains the chasable plan name, add a link to the chasable website
    return (
      <HStack className="flex-wrap items-center justify-center">
        <Text size="sm" className="text-center text-typography-600">
          {parts[0]}
        </Text>
        <Pressable onPress={handleChasableLinkPress} hitSlop={8}>
          <Text size="sm" className="text-primary-500 web:cursor-pointer web:hover:underline">
            {chasablePlanName}
          </Text>
        </Pressable>
        <Text size="sm" className="text-center text-typography-600">
          {parts[1]}
        </Text>
      </HStack>
    );
  };

  return (
    <View className={className}>
      <Pressable onPress={!isPremium ? onSubscribe : undefined} disabled={isPremium}>
      <Card
        size="lg"
        className={`relative overflow-hidden web:transition-all web:duration-200 web:ease-in-out web:hover:scale-105 dark:web:hover:bg-background-100 web:hover:shadow-lg ${!isPremium ? 'web:cursor-pointer' : ''} ${isRecommended ? 'border-2 border-primary-500' : ''}`}>
        <View className="absolute -top-5 -left-11 opacity-10">
          <HugeIconsWrapper
            icon={StarAward01Icon}
            className="text-primary-500"
            size={160}
          />
        </View>
        
        {savings && (
          <View className="absolute top-0 right-0 bg-success-100 px-3 py-1.5 rounded-tr-xl rounded-bl-xl z-10">
            <Text size="xs" className="font-semibold text-success-700 uppercase">{savings}</Text>
          </View>
        )}
        
        <VStack space="lg">
          <VStack space="sm" className="items-center">
            <Heading size="xl" className="text-center text-primary-500">
              {title}
            </Heading>
            {renderSubtitle()}

            <VStack className="items-center">
              <HStack className="items-baseline justify-center">
                
                <Text size="4xl" bold>{dollars}</Text>
                <Text size="xl">.{cents.toString().padStart(2, '0')}</Text>
                <Text size="sm" className="font-medium text-typography-500 ml-1">USD</Text>
              </HStack>

              <Text size="sm" className="text-typography-500 text-center">{period}</Text>
            </VStack>
          </VStack>

        <VStack space="sm" className="items-center">
          <View pointerEvents="none">
            <Button
              variant="solid"
              action="primary"
              isDisabled={isPremium}>
              <ButtonText>{isPremium ? t('account.plans.subscribed') : t('account.plans.subscribe')}</ButtonText>
            </Button>
          </View>
          <Text size="xs" className="text-typography-500 text-center py-2">
            {t('account.plans.renewsEveryDays', { days: renewalDays })}
          </Text>
        </VStack>
        </VStack>
      </Card>
    </Pressable>
    </View>
  );
};

